import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  PawPrint, Search, MapPin, Heart, Phone, Mail, ArrowLeft,
  Check, X, Home, Users, Dog, Filter
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

export default function AdoptionPage() {
  const { dogId } = useParams();
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDog, setSelectedDog] = useState(null);
  const [filters, setFilters] = useState({ city: '', size: '', sex: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (dogId) {
      fetchDogDetail(dogId);
    } else {
      fetchDogs();
    }
  }, [dogId, page]);

  const fetchDogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/associations/public/dogs?page=${page}&limit=12`;
      if (filters.city) url += `&city=${encodeURIComponent(filters.city)}`;
      if (filters.size) url += `&size=${filters.size}`;
      if (filters.sex) url += `&sex=${filters.sex}`;

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setDogs(data.dogs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDogDetail = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/associations/public/dogs/${id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedDog(data);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchDogs();
  };

  // Single dog detail view
  if (selectedDog) {
    return (
      <div className="min-h-screen bg-[#F5F5F7]">
        {/* Header */}
        <header className="bg-white border-b border-black/5 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Link to="/adozioni" className="p-2 hover:bg-gray-100 rounded-xl">
                <ArrowLeft size={20} className="text-[#86868B]" />
              </Link>
              <h1 className="font-bold text-[#1D1D1F]">Torna alla ricerca</h1>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-3xl overflow-hidden shadow-sm">
            {/* Photo */}
            <div className="h-64 sm:h-96 bg-gradient-to-br from-[#FF6B4A]/20 to-[#FF8F6B]/20 flex items-center justify-center">
              {selectedDog.photo_url ? (
                <img 
                  src={selectedDog.photo_url} 
                  alt={selectedDog.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <PawPrint className="text-[#FF6B4A]/50" size={80} />
              )}
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-[#1D1D1F]">{selectedDog.name}</h1>
                  <p className="text-[#86868B]">{selectedDog.breed || 'Meticcio'}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedDog.status === 'disponibile' 
                    ? 'bg-green-100 text-green-700'
                    : selectedDog.status === 'in_adozione'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {selectedDog.status === 'disponibile' ? 'Disponibile' : 
                   selectedDog.status === 'in_adozione' ? 'In adozione' : 'Adottato'}
                </span>
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-[#F5F5F7] rounded-xl p-3 text-center">
                  <p className="text-xs text-[#86868B]">Età</p>
                  <p className="font-semibold text-[#1D1D1F]">
                    {selectedDog.age_years ? `${selectedDog.age_years} anni` : ''}
                    {selectedDog.age_months ? ` ${selectedDog.age_months} mesi` : ''}
                    {!selectedDog.age_years && !selectedDog.age_months && 'N/D'}
                  </p>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-3 text-center">
                  <p className="text-xs text-[#86868B]">Sesso</p>
                  <p className="font-semibold text-[#1D1D1F]">
                    {selectedDog.sex === 'M' ? 'Maschio' : 'Femmina'}
                  </p>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-3 text-center">
                  <p className="text-xs text-[#86868B]">Taglia</p>
                  <p className="font-semibold text-[#1D1D1F] capitalize">{selectedDog.size}</p>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-3 text-center">
                  <p className="text-xs text-[#86868B]">Microchip</p>
                  <p className="font-semibold text-[#1D1D1F]">
                    {selectedDog.microchip ? 'Sì' : 'No'}
                  </p>
                </div>
              </div>

              {/* Health */}
              <div className="flex gap-4 mb-6">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                  selectedDog.vaccinated ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'
                }`}>
                  {selectedDog.vaccinated ? <Check size={16} /> : <X size={16} />}
                  <span className="text-sm">Vaccinato</span>
                </div>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                  selectedDog.neutered ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'
                }`}>
                  {selectedDog.neutered ? <Check size={16} /> : <X size={16} />}
                  <span className="text-sm">Sterilizzato</span>
                </div>
              </div>

              {/* Description */}
              {selectedDog.description && (
                <div className="mb-6">
                  <h3 className="font-semibold text-[#1D1D1F] mb-2">Descrizione</h3>
                  <p className="text-[#86868B]">{selectedDog.description}</p>
                </div>
              )}

              {/* Character */}
              {selectedDog.character && (
                <div className="mb-6">
                  <h3 className="font-semibold text-[#1D1D1F] mb-2">Carattere</h3>
                  <p className="text-[#86868B]">{selectedDog.character}</p>
                </div>
              )}

              {/* History */}
              {selectedDog.history && (
                <div className="mb-6">
                  <h3 className="font-semibold text-[#1D1D1F] mb-2">La sua storia</h3>
                  <p className="text-[#86868B]">{selectedDog.history}</p>
                </div>
              )}

              {/* Suitability */}
              <div className="mb-6">
                <h3 className="font-semibold text-[#1D1D1F] mb-2">Adatto a</h3>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    selectedDog.suitable_for_children 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedDog.suitable_for_children ? '✓' : '✗'} Bambini
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    selectedDog.suitable_for_other_dogs 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedDog.suitable_for_other_dogs ? '✓' : '✗'} Altri cani
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    selectedDog.suitable_for_cats 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedDog.suitable_for_cats ? '✓' : '✗'} Gatti
                  </span>
                  {selectedDog.needs_garden && (
                    <span className="px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-700">
                      Richiede giardino
                    </span>
                  )}
                </div>
              </div>

              {/* Association Contact */}
              {selectedDog.association && (
                <div className="bg-[#E8F5E9] rounded-2xl p-6">
                  <h3 className="font-semibold text-[#1D1D1F] mb-3">Contatta il rifugio</h3>
                  <p className="font-medium text-[#2E7D32] mb-2">{selectedDog.association.name}</p>
                  {selectedDog.association.city && (
                    <p className="text-sm text-[#86868B] flex items-center gap-2 mb-2">
                      <MapPin size={14} /> {selectedDog.association.city}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-4">
                    {selectedDog.association.phone && (
                      <a 
                        href={`tel:${selectedDog.association.phone}`}
                        className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl text-[#2E7D32] font-medium"
                      >
                        <Phone size={16} /> Chiama
                      </a>
                    )}
                    {selectedDog.association.email && (
                      <a 
                        href={`mailto:${selectedDog.association.email}?subject=Adozione ${selectedDog.name}`}
                        className="flex items-center gap-2 px-4 py-2 bg-[#2E7D32] rounded-xl text-white font-medium"
                      >
                        <Mail size={16} /> Scrivi
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dogs list view
  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="p-2 hover:bg-gray-100 rounded-xl">
                <ArrowLeft size={20} className="text-[#86868B]" />
              </Link>
              <div>
                <h1 className="font-bold text-[#1D1D1F] text-lg">Adotta un amico</h1>
                <p className="text-xs text-[#86868B]">{total} cani in cerca di casa</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="text-[#E91E63]" size={20} />
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border-b border-black/5 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                <Input
                  value={filters.city}
                  onChange={(e) => setFilters({...filters, city: e.target.value})}
                  placeholder="Cerca per città..."
                  className="pl-10 h-10 rounded-xl bg-[#F5F5F7] border-0"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </div>
            <Select value={filters.size || "all"} onValueChange={(v) => setFilters({...filters, size: v === "all" ? "" : v})}>
              <SelectTrigger className="w-32 h-10 rounded-xl bg-[#F5F5F7] border-0">
                <SelectValue placeholder="Taglia" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                <SelectItem value="piccola">Piccola</SelectItem>
                <SelectItem value="media">Media</SelectItem>
                <SelectItem value="grande">Grande</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.sex || "all"} onValueChange={(v) => setFilters({...filters, sex: v === "all" ? "" : v})}>
              <SelectTrigger className="w-32 h-10 rounded-xl bg-[#F5F5F7] border-0">
                <SelectValue placeholder="Sesso" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="M">Maschio</SelectItem>
                <SelectItem value="F">Femmina</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} className="h-10 bg-[#E91E63] hover:bg-[#C2185B] rounded-xl">
              <Filter size={16} className="mr-2" /> Filtra
            </Button>
          </div>
        </div>
      </div>

      {/* Dogs Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-3 border-[#E91E63] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : dogs.length === 0 ? (
          <div className="text-center py-20">
            <Dog className="mx-auto text-[#86868B] mb-4" size={64} />
            <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">Nessun cane trovato</h3>
            <p className="text-[#86868B]">Prova a modificare i filtri di ricerca</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {dogs.map(dog => (
              <Link
                key={dog.dog_id}
                to={`/adozioni/${dog.dog_id}`}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group"
              >
                {/* Photo */}
                <div className="h-48 bg-gradient-to-br from-[#FCE4EC] to-[#F8BBD9] flex items-center justify-center overflow-hidden">
                  {dog.photo_url ? (
                    <img 
                      src={dog.photo_url} 
                      alt={dog.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <PawPrint className="text-[#E91E63]/30" size={48} />
                  )}
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-[#1D1D1F] group-hover:text-[#E91E63] transition-colors">
                        {dog.name}
                      </h3>
                      <p className="text-sm text-[#86868B]">{dog.breed || 'Meticcio'}</p>
                    </div>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Disponibile
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-[#86868B]">
                    <span>{dog.sex === 'M' ? 'Maschio' : 'Femmina'}</span>
                    <span>•</span>
                    <span className="capitalize">{dog.size}</span>
                    {dog.association_city && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <MapPin size={10} /> {dog.association_city}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 12 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button 
              variant="outline" 
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="rounded-xl"
            >
              Precedente
            </Button>
            <span className="px-4 py-2 text-[#86868B]">Pagina {page}</span>
            <Button 
              variant="outline" 
              disabled={dogs.length < 12}
              onClick={() => setPage(p => p + 1)}
              className="rounded-xl"
            >
              Successiva
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
